import { isNumber } from "mathjs";
import { Scene } from "three";
import Sequence from "./Sequence";
import Step from "./Step";
import store, { StoreActions } from "../../state/store";
import { ChecklistFormType, ChecklistPriority, CustomEventTypes, SimulationStates, UISidebarContentTypes } from "../../globals";
import { assert } from "../utilities/ErrorHandling";
import StepConditionsManager from "../StepConditionsManager";
import { toggleSidebarContent } from "../../components/uisystem/UIUtilities";
import ScenarioHealthUtils from "../utilities/ScenarioHealthUtils";
import { ProjectOptions, ScenarioDataObject, ScenarioSaveData, ScenarioExportData, IProjectInfo } from "../../interfaces/ScenarioElements";
import TimeUtils from "../utilities/TimeUtils";
import { RegisterClassWithAPI} from "../api/Decorators";
import { ITrackerSaveData } from "../../interfaces/ProjectSave";
import { ScenarioElementTypes } from "../../interfaces/enums";
import { getUuidv4 } from "../utilities/Misc";
import UIUtils from "../utilities/UIUtils";
import { scenarioMap } from ".";

const scenarioDataObject : ScenarioDataObject = {
	id: "",
	name: "",
	type: ScenarioElementTypes.Scenario,
	creationDate: TimeUtils.getDateFromGMTString(new Date().toUTCString()),
	externalId: "",
	version: 0,
	originalAuthor: "",
};

/**
 * represents a complete work instructions set, holding all the information necessary for playback in the viewer app
 *
 * also contains useful helper static functions
 * @export
 * @class Scenario
 */
@RegisterClassWithAPI("Scenario", {}, "Scenario cannot be created. Use GetCurrentScenario to obtain the current scenario", false)
export default class Scenario
{
	//#region Static Functions

	/**
	 *
	 *
	 * @static
	 * @param {*} elementId
	 * @returns
	 * @memberof Scenario
	 */
	static getElement<T extends Scenario | Sequence | Step>(elementId : string) : T | undefined
	{
		const element = scenarioMap.get(elementId);
		if(!element) return;

		return element as T;
	}

	/**
	 *
	 *
	 * @static
	 * @param {*} stepId
	 * @returns
	 * @memberof Scenario
	 */
	static getStep(stepId : string) : Step | undefined
	{
		return Scenario.getElement(stepId);
	}

	/**
	 *
	 *
	 * @static
	 * @memberof Scenario
	 */
	static getCPS() : Step | undefined
	{
		return Scenario.initialSequence()?.getTrackersStep();
	}

	/**
	 *
	 *
	 * @static
	 * @param {*} sequenceId
	 * @returns
	 * @memberof Scenario
	 */
	static getSequence(sequenceId: string) : Sequence | undefined
	{
		return Scenario.getElement(sequenceId);
	}

	/**
	 * @static
	 * @returns the currently active scenario from Redux (there can be only one)
	 * @memberof Scenario
	 */
	static current() : Scenario | undefined
	{
		assert(store, "redux storage must exist before attempting to read data from it");

		const currentScenarioId = store.getState().scenarioModule.scenarioId || "";
		return Scenario.getElement(currentScenarioId);
	}

	static getStepBySceneId(sceneId: string) : Step | null
	{
		let foundStep: Step | null = null;
		Scenario.current()?.sequences.forEach((sequence) =>
		{
			if(sequence.defaultStep.sceneId === sceneId)
			{
				foundStep = sequence.defaultStep;
				return;
			}

			sequence.steps.forEach((step) =>
			{
				if (step.sceneId === sceneId)
				{
					foundStep = step;
					return;
				}
			});
		});

		return foundStep;
	}

	/**
	 * @static
	 * @returns an array of all the scenes associated with non-deleted steps in the scenario
	 * @memberof Scenario
	 */
	 static getAllScenes()
	 {
		 return Scenario.current()?.getScenes() || [];
	 }

	/**
	 * @static
	 * @returns the step that is currently being edited
	 * @memberof Scenario
	 */
	static currentStep() : Step | undefined
	{
		assert(store, "redux storage must exist before attempting to read data from it");

		const stepId = store.getState().scenarioModule.currentStepId || "";
		return Scenario.getElement(stepId);
	}


	/**
	 * @static
	 * @returns a reference to the sequence object that is marked as the Initial Sequence in the scenario
	 * @memberof Scenario
	 */
	static initialSequence() : Sequence | undefined
	{
		assert(store, "redux storage must exist before attempting to read data from it");

		const initialSequenceId = store.getState().scenarioModule.initialSequenceId || Scenario.getSequences()[0]?.id;
		return Scenario.getElement(initialSequenceId);
	}

	/**
	 * @static
	 * @returns the sequence that is currently being edited
	 * @memberof Scenario
	 */
	 static currentSequence() : Sequence | undefined
	 {
		assert(store, "redux storage must exist before attempting to read data from it");

		return Scenario.currentStep()?.sequence;
	 }

	/**
	 * @static
	 * @returns an array of all non-deleted steps (excluding default state steps) from all sequences in the scenario
	 * @memberof Scenario
	 */
	static getSteps(includeTrackersStep = false) : Step[]
	{
		const output: Step[] = [];
		const currentScenario = Scenario.current();
		if (!currentScenario) return [];
		for (let i = 0; i < currentScenario.sequences.length; i++)
		{
			for (let j = 0; j < currentScenario.sequences[i].steps.length; j++)
			{
				const step = currentScenario.sequences[i].steps[j];
				if(!includeTrackersStep && step.isTrackersStep()) continue;

				output.push(step);
			}
		}
		return output;
	}

	/**
	 * @static
	 * @returns go through all the steps and clear any simulation skip there is (used for content placement steps that should be skipped after viewed once)
	 * @memberof Scenario
	 */
	static clearSimulationSkips() : void
	{
		const steps = Scenario.getSteps();
		steps.forEach((step) =>
		{
			step.simulationSkip = false;
		});
	}

	/**
	 * @static
	 * @returns a set of all the selected steps (excluding default steps) from all sequences in the scenario
	 * @memberof Scenario
	 */
	static getSelectedSteps() : string[]
	{
		return store.getState().scenarioModule.selectedSteps;
	}

	/**
	 * @static
	 * @returns an array of all non-deleted sequences in the scenario
	 * @memberof Scenario
	 */
	static getSequences() : Sequence[]
	{
		return Scenario.current()?.sequences || [];
	}

	/**
	 * search if the given step exists within the scenario and is not deleted
	 *
	 * internally uses Scenario.getSteps
	 *
	 * uses Scenario.getStep when 'step' is a guid and not a direct reference
	 * @static
	 * @param {Step | String} step a direct Step reference, or a guid of a Step instance
	 * @returns true if the provided step exists within the scenario and is not deleted
	 * @memberof Scenario
	 */
	static hasStep(step : Step | string) : boolean
	{
		let result = true;
		let _step: Step | undefined = step as Step;
		if (typeof (step) === "string")
		{
			_step = Scenario.getStep(step);
		}

		if (!_step || _step.isDeleted() || Scenario.getSteps().indexOf(_step) < 0)
		{result = false;}

		return result;
	}

	/**
	 * removes all sequences from the scenario (but doesn't delete or dispose them)
	 * @static
	 * @memberof Scenario
	 */
	static clear() : void
	{
		const scenario = Scenario.current();
		if (scenario)
		{
			scenario.clear();
		}
	}

	/**
	 * actually dispose of all sequences and steps of the scenario, removing them from existence
	 *
	 * also takes care of clearing Redux scenario data
	 * @static
	 * @memberof Scenario
	 */
	static dispose() : void
	{
		Scenario.current()?.dispose();
		store.dispatch(StoreActions.scenario.clearScenario());
	}

	/**
	 * @static
	 * @returns a data structure formatted with all the necessary information for viewer app playback
	 * @memberof Scenario
	 */
	static export(objectGuidToNodeIndexMap: Map<string, number> | undefined = undefined): ScenarioExportData
	{
		const scenario = Scenario.current();
		assert(scenario, "Can't export a non-existent scenario");
		
		return scenario!.export(objectGuidToNodeIndexMap);
	}

	/**
	 * @static
	 * @returns a data structure formatted with all the necessary information to save and load the scenario
	 * @memberof Scenario
	 */
	static save(): ScenarioSaveData
	{
		const scenario = Scenario.current();
		assert(scenario, "Can't save a non-existent scenario");
		
		return scenario!.save();
	}

	/**
	 * adds a sequence to the scenario
	 *
	 * handles UI and redux data as well
	 * @static
	 * @param {Sequence} sequence the sequence object instance to be added to the scenario
	 * @memberof Scenario
	 */
	static addSequence(sequence : Sequence) : void
	{
		assert(sequence, "can't add non-existent sequence");

		const scenario = Scenario.current();
		assert(scenario, "Can't add a sequence to a non-existent scenario");
		
		if(!scenario || !sequence) return;

		scenario.sequences.push(sequence);

		const sequencesTopbarDraw = [...store.getState().scenarioModule.sequencesTopbarDraw];
		sequencesTopbarDraw.splice(sequence.index || sequence.index === 0 ? sequence.index : sequencesTopbarDraw.length, 0, sequence.id);

		store.dispatch(StoreActions.scenario.setSequencesTopbarDraw(sequencesTopbarDraw));
		store.dispatch(StoreActions.scenario.updateScenario());

		ScenarioHealthUtils.CheckStep(sequence.defaultStep);
		for(const step of sequence.steps)
		{
			ScenarioHealthUtils.CheckStep(step);
		}
	}


	/**
	 * removes the specified sequence from the scenario (but doesn't really deletes it so we can undo)
	 *
	 * handles UI and redux data as well
	 * @static
	 * @param {Sequence} sequence the sequence object instance to be removed from the scenario
	 * @memberof Scenario
	 */
	static removeSequence(sequence : Sequence) : void
	{
		assert(sequence, "Can't remove non-existent sequence");

		const scenario = Scenario.current();
		assert(scenario, "can't remove a sequence from a non-existent scenario");
		
		if(!scenario || !sequence) return;

		ScenarioHealthUtils.RemoveStepReports(sequence.defaultStep);
		for(const step of sequence.steps)
		{
			ScenarioHealthUtils.RemoveStepReports(step);
		}

		const seqIndex = scenario.sequences.indexOf(sequence);
		const leftSeq = scenario.sequences[seqIndex > 0 ? Math.max(0, seqIndex - 1) : Math.min(1, seqIndex + 1)];

		// set current step to the immediate left - if we were the current sequence
		if (Scenario.currentStep()?.sequence === sequence)
		{
			Scenario.setCurrentStep(leftSeq.steps[0]);
		}

		// set initial sequence to immediate left, if it was the deleted one
		if (Scenario.initialSequence() === sequence)
		{
			//state.initialSequence = leftSeq;
			store.dispatch(StoreActions.scenario.setInitialSequence(leftSeq.id));
		}

		// remove sequence from scenario
		scenario.sequences = scenario.sequences.filter((seq) => seq.id !== sequence.id);

		let sequencesTopbarDraw = [...store.getState().scenarioModule.sequencesTopbarDraw];
		sequencesTopbarDraw = sequencesTopbarDraw.filter((item, idx) => item !== sequence.id);

		store.dispatch(StoreActions.scenario.setSequencesTopbarDraw(sequencesTopbarDraw));
		store.dispatch(StoreActions.scenario.updateScenario());
	}

	/**
	 * Adds the given step to the given sequence
	 * @static
	 * @param {Sequence} sequence
	 * @param {Step} step
	 * @memberof Scenario
	 */
	static addStep(sequence: Sequence, step: Step, index: number | null = null)
	{
		assert(sequence, "can't add a step to a non-existent sequence");
		assert(step, "can't add non-existent step to a sequence");

		sequence.steps.push(step);

		// Set the step's sequence back-reference
		step.sequence = sequence;

		if(isNumber(index))
		{
			sequence.moveStep(step.getIndex(), index);
		}

		store.dispatch(StoreActions.scenario.updateScenario());
		ScenarioHealthUtils.CheckStep(step);
	}

	/**
	 * removes the given step from the given sequence
	 * @static
	 * @param {Sequence} sequence
	 * @param {Step} step
	 * @memberof Scenario
	 */
	static removeStep(sequence : Sequence, step : Step) : void
	{
		assert(sequence, "can't remove a step from a non-existent sequence");
		assert(step, "can't remove non-existent step from a sequence");

		if (Scenario.initialSequence()?.id === sequence.id)
		{
			if(sequence.steps.length <= 2) // CPS + normal step, presumably?
				return;
		}
		else
		{
			if(sequence.steps.length <= 1)
				return;
		}

		// set the current step to immediate left - if the removed step was the current step
		if (Scenario.currentStep() === step)
		{
			const stepIndex = sequence.steps.indexOf(step);
			const newCurrentStep = sequence.steps[stepIndex > 0 ? Math.max(0, stepIndex - 1) : Math.min(1, stepIndex + 1)];
			Scenario.setCurrentStep(newCurrentStep);
		}

		// remove step from sequence
		sequence.steps = sequence.steps.filter((s) => s.id !== step.id);
		store.dispatch(StoreActions.scenario.updateScenario());
		ScenarioHealthUtils.RemoveStepReports(step);
	}

	/**
	 * marks the given Step as the currently active one
	 * @static
	 * @param {Step} step
	 * @memberof Scenario
	 */
	static setCurrentStep(step: Step, ignoreRules = false, addToHistory = true) : void
	{
		assert(step, "step should never be null or undefined");
		if(UIUtils.isEditingVisageTemplate() && !ignoreRules)
		{
			UIUtils.generateExitVisageTemplateEditorDialog(() => Scenario.setCurrentStep(step, ignoreRules, addToHistory));
			return;
		}

		function setInRedux(step : Step)
		{
			//console.log(step);
			store.dispatch(StoreActions.scenario.setCurrentStep(step.id));
			if (!step.isDefault())
			{
				step.sequence.recentStep = step;
			}

			store.dispatch(StoreActions.scenario.updateScenario());
			document.dispatchEvent(new CustomEvent(CustomEventTypes.onElementNameChanged));
		}

		//utils
		if (ignoreRules) // if true, only push to redux and return
		{
			//console.log("ignore rules");
			setInRedux(step);
			return;
		}

		const simulationState = store.getState().applicationModule.simulationState;
		if (simulationState === SimulationStates.Playing)
		{
			//console.log("simulation playing");
			if (step.isDisabledTrackersStep()) return;
		}

		//Tracker Step
		if (simulationState !== SimulationStates.Playing && step.trackers && step.trackers.length > 0)
		{
			//console.log("simulation NOT playing AND we have trackers");
			setInRedux(step);
		}

		// checklist
		const currentStep = Scenario.currentStep();
		const stepChecklist = currentStep ? store.getState().checklistModule.checklist[currentStep.id] : null;
		let hasChecklistItems = false;
		if (stepChecklist &&
			stepChecklist.type !== ChecklistFormType.NONE &&
			stepChecklist.behaviour === ChecklistPriority.FORCEVIEW &&
			(stepChecklist.items.length > 0 || stepChecklist.questions.length > 0))
		{
			hasChecklistItems = true;
		}

		//if step has a checklist that has to be force played at the end of the step play it without changing the step
		if (simulationState === SimulationStates.Playing && stepChecklist && stepChecklist.behaviour === ChecklistPriority.FORCEVIEW && hasChecklistItems)
		{
			toggleSidebarContent(UISidebarContentTypes.Form, { simulationEvent: ChecklistPriority.FORCEVIEW, next: step?.id ? step.id : "" });
		}
		else
		{
			if (simulationState === SimulationStates.Playing && addToHistory && currentStep)
			{
				//console.log("simulation playing BUT there's NO checklist");
				store.dispatch(StoreActions.scenario.addStepToHistory(currentStep.id));
			}

			setInRedux(step);
		}

		if (simulationState === SimulationStates.Playing)
		{
			StepConditionsManager.setStepConditions();
		}
	}

	static goBackInStepHistory(amount  = 1) : void
	{
		const stepHistory = store.getState().scenarioModule.stepHistorySimulation;
		if (stepHistory.length < amount || amount < 1)
		{
			return;
		}

		let stepId = stepHistory[stepHistory.length - amount];
		let step = Scenario.getStep(stepId);
		if (step?.simulationSkip)
		{
			if (stepHistory.length >= amount + 1)
			{
				amount++;
			}
			else
			{
				return;
			}
		}

		stepId = stepHistory[stepHistory.length - amount];
		step = Scenario.getStep(stepId) || Scenario.initialSequence()?.steps[0];
		if(!step)
		{
			console.error("Step not found in history", step);
			return;
		}

		Scenario.setCurrentStep(step, false, false);
		store.dispatch(StoreActions.scenario.goBackInStepHistory(amount));
	}

	//#endregion

	#id: string;
	sequences: Sequence[] = [];
	
	/**
	 * Creates an instance of Scenario
	 * 
	 * To automatically generate a sequence and steps as well, pass `autogenerate: true` in the options object
	 * @param {IProjectInfo} projectInfo
	 * @param {ProjectOptions} [options={}]
	 * @memberof Scenario
	 */
	constructor(projectInfo: IProjectInfo, options: ProjectOptions = {})
	{
		assert(projectInfo);
		//console.log(projectInfo);

		this.#id = projectInfo._id;

		const dataObject : ScenarioDataObject = {
			...scenarioDataObject,
			id: this.id,
			name: projectInfo.name,
			creationDate: projectInfo.creation_date,
			externalId: projectInfo.external_id,
			version: projectInfo.__v,
			originalAuthor: projectInfo.originalAuthor,
		};
		store.dispatch(StoreActions.scenario.setElementData({ dataId: this.id, data: dataObject }));

		this.sequences = [];
		if (options.autogenerate)
		{
			this.sequences.push(new Sequence(this));
		}

		store.dispatch(StoreActions.scenario.setScenarioPublishInfo(projectInfo.publish_info!));
		scenarioMap.set(this.id, this);
	}

	getDataObject() : ScenarioDataObject
	{
		return store.getState().scenarioModule.elementData[this.id];
	}

	get id() : string
	{
		return this.#id;
	}

	set id(newId : string)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "id", propertyValue: newId }));
		this.#id = newId;
		scenarioMap.set(newId, this);

		this.sequences.forEach((sequence) => sequence.parentId = newId);
	}

	get name() : string
	{
		return this.getDataObject().name;
	}

	set name(newName : string)
	{
		store.dispatch(StoreActions.scenario.setElementProperty({ elementId: this.id, propertyName: "name", propertyValue: newName }));
	}

	get creationDate() : string | undefined
	{
		return this.getDataObject().creationDate;
	}

	get externalId() : string | undefined
	{
		return this.getDataObject().externalId;
	}

	get version() : number | undefined
	{
		return this.getDataObject().version;
	}

	get originalAuthor() : string | undefined
	{
		return this.getDataObject().originalAuthor;
	}

	getType() : string
	{
		return ScenarioElementTypes.Scenario;
	}

	getChildren() : Sequence[]
	{
		return this.sequences;
	}

	countSteps() : number
	{
		let counter = 0;
		for (const sequence of this.sequences)
		{
			//subtracting 1 from steps count to remove all unused CPS of every sequence
			counter+= (sequence.steps.length - 1);
		}

		return counter;
	}

	getInitialSequence()
	{
		if(this.id === Scenario.current()?.id)
		{
			return Scenario.initialSequence();
		}
		
		return this.sequences[0];
	}

	/**
* @returns all the data necessary to save and load this scenario from a file
* @memberof Scenario
*/
	save() : ScenarioSaveData
	{
		const output : ScenarioSaveData = {
			currentStep: store.getState().scenarioModule.currentStepId!,
			initialSequence: store.getState().scenarioModule.initialSequenceId!,
			sequences: {},
			steps: {},
			sequencesTopbarDraw: store.getState().scenarioModule.sequencesTopbarDraw
		};

		// serialize sequences
		this.sequences.forEach((sequence) =>
		{
			output.sequences[sequence.id] = sequence.save();
			output.steps[sequence.defaultStep.id] = sequence.defaultStep.save();
			sequence.steps.forEach((step) =>
			{
				output.steps[step.id] = step.save();
			});
		});

		return output;
	}

	/**
* @returns all the data necessary for playback in the viewer app
* @memberof Scenario
*/
	export(objectGuidToNodeIndexMap: Map<string, number> | undefined = undefined) : ScenarioExportData
	{
		const output : ScenarioExportData = {
			initialSequence: store.getState().scenarioModule.initialSequenceId!,
			sequences: {},
			steps: {}
		};

		// serialize sequences
		this.sequences.forEach((sequence) =>
		{
			output.sequences[sequence.id] = sequence.export();
			sequence.steps.forEach((step) =>
			{
				if (!step.isTrackersStep() || (step.isTrackersStep() && step.id === sequence.trackersStep.id))
				{output.steps[step.id] = step.export(objectGuidToNodeIndexMap);}
			});
		});

		return output;
	}

	/**
 * constructs a scenario structure from save data
 * @param {*} data
 * @memberof Scenario
 */
	load(data : ScenarioSaveData, trackersData : ITrackerSaveData[])
	{
		assert(data);
		// console.log(data);

		this.clear();
		//scenarioMap.delete(this.id);

		const rawSequencesOrder : string[] = data.sequencesTopbarDraw ?? [];
		const sequencesKeys : string[] = Object.keys(data.sequences);
		const sequencesTopbarDraw : string[] = [];

		for (const key of rawSequencesOrder)
		{
			if (sequencesKeys.includes(key))
			{
				sequencesTopbarDraw.push(key);
			}
		}

		// load sequences
		for (const key of sequencesKeys)
		{
			if (!sequencesTopbarDraw.includes(key))
			{
				sequencesTopbarDraw.push(key);
			}
			const sequenceData = data.sequences[key];

			const newSequence = new Sequence(this, { autogenerate: false });
			newSequence.load(sequenceData);

			// load default step
			const defaultStepData = data.steps[sequenceData.defaultStep];
			const defaultStep = new Step(newSequence, {
				connectedToDefault: false,
				isDefaultStep: true,
				autogenerate: false,
			});
			defaultStep.load(defaultStepData);
			newSequence.defaultStep = defaultStep;

			// load the rest of the steps
			for (const stepId of sequenceData.stepIds)
			{
				const stepData = data.steps[stepId];
				const newStep = new Step(newSequence, { autogenerate: false });
				newStep.load(stepData);
				newSequence.steps.push(newStep);
			}

			assert(newSequence.steps.length);
			newSequence.recentStep = newSequence.steps[0];
			this.sequences.push(newSequence);
		}

		// set the tracker steps
		const sequencesWithNoTrackersStep : Sequence[] = [];
		for (const key of Object.keys(data.sequences))
		{
			const sequence = Scenario.getSequence(key);
			if ( sequence ) 
			{
				if (data.sequences[key].trackersStep)
				{
					sequence.trackersStep = Scenario.getStep(data.sequences[key].trackersStep)!;
				}
				else
				{
					sequencesWithNoTrackersStep.push(sequence);
				}
			}
		}

		let initialSequence = Scenario.getSequence(data.initialSequence);

		if (sequencesWithNoTrackersStep.length > 0)
		{
			//check the initial sequence
			//console.log("Found Sequences with no trackers steps ",sequencesWithNoTrackersStep);
			let initialSequenceTrackersStep : Step | undefined;
			if (!data.initialSequence || !data.sequences[data.initialSequence])
			{data.initialSequence = sequencesWithNoTrackersStep[0].id;}

			if (!data.sequences[data.initialSequence].trackersStep)
			{
				//console.log("Initial Sequence has no trackers-step. Creating one... ");
				//initial sequence has no trackers step, create a new one and assign all the trackers to it.
				initialSequence = Scenario.getSequence(data.initialSequence);
				if ( initialSequence ) 
				{
					initialSequenceTrackersStep = new Step(initialSequence, {
						autogenerate: true,
						isTrackersStep: true,
						dontMakeTracker: trackersData && trackersData.length > 0 ? true : false,
					});

					if (trackersData && trackersData.length > 0) 
					{
						//console.log("Assiging existing trackers to the initial trackers-step");
						for (const tracker of trackersData) 
						{
							if (!tracker.id) 
							{
								//console.log("Tracker ", tracker.name, 'has no ID, generating one...');
								tracker.id = getUuidv4();
							}
							//console.log("Assiging tracker ",tracker);
							initialSequenceTrackersStep.trackers!.push(tracker.id);
						}
					}

					//console.log("Setting new trackers-step in the initial sequences step list");
					initialSequence.steps.unshift(initialSequenceTrackersStep);
					initialSequence.trackersStep = initialSequenceTrackersStep;
				}
			}
			else
			{
				//console.log("Initial Sequence has a trackers-step. Continuing...");
				initialSequenceTrackersStep = Scenario.getStep(data.sequences[data.initialSequence].trackersStep);
			}

			assert(initialSequenceTrackersStep, "Couldn't get initial trackers-step");
			for (const sequence of sequencesWithNoTrackersStep)
			{
				if (sequence.id === data.initialSequence)
				{
					continue;
				}

				if ( initialSequenceTrackersStep ) 
				{
					console.log("Assigning initial sequences trackers-step to sequence ", sequence.name);
					sequence.trackersStep = initialSequenceTrackersStep;
				}
			}
		}

		//making sure no sequence has multiple tracker steps
		// console.log('Multiple CPS checker')
		for (const sequence of this.sequences)
		{
			const stepsToDispose : Step[] = [];
			for (const step of sequence.steps)
			{

				if (step.isTrackersStep() && step.id !== sequence.trackersStep.id )
				{
					// sequence.trackersStep.trackers = sequence.trackersStep.trackers.concat(step.trackers);
					stepsToDispose.push(step);
				}
			}
			for (const stepToDispose of stepsToDispose)
			{
				console.log('Disposing step ', stepToDispose.name, ' from sequence ', sequence.name);
				stepToDispose.dispose();
			}
		}

		assert(this.sequences.length);

		// apply connections
		for (const sequenceId of Object.keys(data.sequences))
		{
			const sequenceData = data.sequences[sequenceId];
			const sequence = Scenario.getElement<Sequence>(sequenceId);
			if(!sequence) continue;

			sequence.output = Scenario.getElement<Sequence>(sequenceData.outObject);
			store.dispatch(StoreActions.scenario.setElementProperty({elementId: sequence.id, propertyName: "outputId", propertyValue: sequence.output && sequence.output.id ?sequence.output.id : ""}));
		}

		store.dispatch(StoreActions.scenario.setScenario(this.id));
		store.dispatch(StoreActions.scenario.setSequencesTopbarDraw(sequencesTopbarDraw));

		const initSequence = Scenario.getElement<Sequence>(data.initialSequence) ?? this.sequences[0];
		store.dispatch(StoreActions.scenario.setInitialSequence(initSequence.id));

		const currentStep = Scenario.getElement<Step>(data.currentStep) || initSequence.steps.find(step => !step.isTrackersStep());
		if(!currentStep) throw new Error("Couldn't find a current step");

		Scenario.setCurrentStep(currentStep);
	}

	clear() : void
	{
		this.sequences.length = 0;
	}

	/**
 * permanently gets rid of all scenario data
 * @memberof Scenario
 */
	dispose() : void
	{
		this.sequences.forEach((s) => s.dispose());
		scenarioMap.delete(this.id);
	}

	/**
 * @returns an array of all scenes of all non-deleted steps in all non-deleted sequences, including default state steps
 * @memberof Scenario
 */
	getScenes() : Scene[]
	{
		const scenes : Scene[] = [];
		this.sequences.forEach((sequence) =>
		{
			scenes.push(sequence.defaultStep.scene);
			sequence.steps.forEach((step) =>
			{
				scenes.push(step.scene);
			});
		});

		return scenes;
	}

	/**
 * removes a sequence from the playback information, but does not permanently deletes it
 * @param {Sequence} target the sequence to remove
 * @memberof Scenario
 */
	removeSequence(target : Sequence) : void
	{
		this.sequences = this.sequences.filter((sequence) => sequence !== target);
	}

	/**
 * reverses the order of all sequences, and the order of all steps within each sequence, and the playback information of each step
 * @memberof Scenario
 */
	reverse() : void
	{
		this.sequences.reverse();
		this.sequences.forEach((sequence) => sequence.reverse());
	}

	/**
	 * a helper function to find the previous sequence to the given one for navigation purposes
	 *
	 * there can theoretically be multiple sequences that lead up to the `from` sequence, so we return the first one we find
	 * @param {Sequence} from the sequence to find the previous one of
	 * @returns the sequence previous to the given 'from' sequence, or *null* if the given sequence is the initial sequence of the scenario
	 * @memberof Scenario
	 */
	getPreviousSequence(from : Sequence) : Sequence | undefined
	{
		if (from.id === Scenario.initialSequence()?.id) return;	

		for(const sequence of Scenario.getSequences())
		{
			if (sequence.output?.id === from.id)
			{
				return sequence;
			}
		}
	}
}
